import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

// What Tab can land on. Disabled and hidden controls are dropped below.
const FOCUSABLE =
  'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusableIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      // :disabled also matches a control disabled by its <fieldset>.
      !el.matches(":disabled") &&
      !el.closest("[inert]") &&
      // Drops controls hidden at this screen size (hidden sm:block) or by
      // visibility: hidden. Where checkVisibility is missing, a control is
      // taken as visible.
      (typeof el.checkVisibility !== "function" ||
        el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true } as CheckVisibilityOptions)),
  );
}

// Open dialogs, oldest first. Only the last is interactive: every other child
// of <body>, earlier dialogs included, is inert. One list rather than each
// dialog marking the rest, so two dialogs that open in the same render cannot
// each make the other inert.
//
// Ranked by the order the dialogs rendered open in, not the order their
// effects ran: React runs a child's effects before its parent's, so a dialog
// nested open inside another that opens in the same render would otherwise
// end up under it.
const openOverlays: { el: HTMLElement; rank: number }[] = [];
let lastRank = 0;
// What we made inert, so that only that is ever released: something inert for
// its own reasons stays inert.
const madeInert = new Set<Element>();
let bodyWatcher: MutationObserver | null = null;

function syncInert() {
  const top = openOverlays[openOverlays.length - 1]?.el;
  for (const el of Array.from(document.body.children)) {
    if (top && el !== top) {
      if (!el.hasAttribute("inert")) {
        el.setAttribute("inert", "");
        madeInert.add(el);
      }
    } else if (madeInert.has(el)) {
      el.removeAttribute("inert");
      madeInert.delete(el);
    }
  }
  // Released anything that has since left the page.
  for (const el of madeInert) if (!el.isConnected) madeInert.delete(el);

  // Something added to <body> while a dialog is open is outside it too: the
  // bill's print frame lands there when its document arrives, which can be
  // after a dialog opened. Watched only while a dialog is open. The recount
  // above works from the open list, not from what changed, so it is right
  // whether this or a new dialog's own effect runs first.
  if (top && !bodyWatcher) {
    bodyWatcher = new MutationObserver(syncInert);
    bodyWatcher.observe(document.body, { childList: true });
  } else if (!top && bodyWatcher) {
    bodyWatcher.disconnect();
    bodyWatcher = null;
  }
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = "md",
}: ModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const rank = useRef(0);
  const opener = useRef<HTMLElement | null>(null);

  // Taken while rendering, parent before child, for the reason at
  // openOverlays. What had focus is read here too: by the time a parent's
  // effect runs, a child dialog opened with it has already taken focus.
  if (isOpen && rank.current === 0) {
    rank.current = ++lastRank;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  // While the dialog is open, everything else on the page is inert: it can't
  // take focus or a click, and a screen reader leaves it out. This holds even
  // when focus drops to <body> because the control that had it was removed,
  // which the Tab handling below can't see.
  //
  // Focus moves into the dialog when it opens (unless a field inside has
  // already taken it, e.g. autoFocus) and back to what had it when it closes.
  // On close the page is released first: the opener may be in a dialog this
  // one made inert, and an inert control can't take focus.
  useEffect(() => {
    if (!isOpen) return;
    const restoreTo = opener.current;
    const overlay = overlayRef.current;
    if (overlay) {
      openOverlays.push({ el: overlay, rank: rank.current });
      openOverlays.sort((a, b) => a.rank - b.rank);
      syncInert();
    }
    // A parent whose effect runs after its child's is inert by now, so this
    // can't pull focus back out of the child.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
    return () => {
      rank.current = 0;
      if (overlay) {
        const at = openOverlays.findIndex((o) => o.el === overlay);
        if (at !== -1) openOverlays.splice(at, 1);
        syncInert();
      }
      // Back to what opened it. Failing that (it was removed, or the dialog
      // opened with its parent and had no opener of its own), to the dialog
      // now on top, so focus is not left on the inert page.
      if (restoreTo?.isConnected) restoreTo.focus();
      if (!restoreTo || restoreTo === document.body || document.activeElement !== restoreTo) {
        openOverlays[openOverlays.length - 1]?.el.querySelector<HTMLElement>('[role="dialog"]')?.focus();
      }
    };
  }, [isOpen]);

  // Tab and Shift+Tab cycle inside the dialog, and Escape closes it. Keys
  // from a dialog opened inside this one reach here too, because React
  // bubbles through portals; they are that dialog's to handle.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel || !panel.contains(e.target as Node)) return;
    if (e.key === "Escape") {
      // A control inside that handled Escape itself, and said so with
      // preventDefault, keeps it.
      if (e.defaultPrevented || e.nativeEvent.isComposing) return;
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const items = focusableIn(panel);
    if (items.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  // Caps from sm up only: below it a dialog is a sheet as wide as the phone.
  const sizeClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
    "3xl": "sm:max-w-3xl",
    "4xl": "sm:max-w-4xl",
  };

  // Rendered into <body>, so no parent can hide it or push it about: a
  // dialog opened from a menu that closes, or from inside a page's space-y-*
  // container, still shows, centred.
  return createPortal(
    // !mt-0: a Modal is often rendered as a later child of a page's space-y-*
    // container, whose top margin would push the whole overlay down the screen.
    <div ref={overlayRef} className="fixed inset-0 z-50 overflow-y-auto !mt-0" onKeyDown={onKeyDown}>
      {/* Backdrop */}
      <div
        data-testid="modal-backdrop"
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal. On a phone, a sheet across the bottom of the screen: as tall
          as its content up to the full screen, the body scrolling between a
          header and footer that stay put. From sm up, a centred card. */}
      <div className="flex min-h-full items-end sm:items-center justify-center sm:p-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`relative w-full ${sizeClasses[size]} card bg-surface outline-none flex flex-col max-h-[100dvh] rounded-b-none pb-[env(safe-area-inset-bottom)] sm:block sm:max-h-none sm:rounded-b-lg sm:pb-0`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
            <h3 id={titleId} className="text-lg font-semibold text-text">{title}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label={t("common.close")}
              icon={<X className="h-4 w-4" />}
            />
          </div>

          {/* Body */}
          <div className="p-4 min-h-0 overflow-y-auto sm:overflow-visible">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex justify-end gap-2 p-4 border-t border-border shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
